// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core'
import * as React from 'react'
import { Form, Formik } from 'formik'
import Button from 'react-bootstrap/lib/Button'
import Modal from 'react-bootstrap/lib/Modal'
import Glyphicon from 'react-bootstrap/lib/Glyphicon'
import Label from 'react-bootstrap/lib/Label'
import { Mutation, Query } from 'react-apollo'
import pluralize from 'pluralize'

import type { CommonActionProps } from './types.actions'
import type { ActionStatus, GridItem } from '../../types'

import ToggleController from '../../controllers/ToggleController'
import StatusController from '../../controllers/StatusController'
import ButtonAction from './ButtonAction'
import Row from '../Row'
import Col from '../Col'
import DescriptionField from '../DescriptionField'
import SingleField from '../SingleField'
import StatusMessage from '../StatusMessage'
import Prompt from '../Prompt'
import Loading from '../Loading'
import { addItemToOrderMutation } from '../../graphql/mutations/orders/addItemToOrder.mutation'
import getActiveOrderQuery from '../../graphql/queries/getActiveOrder.query'
import getPriceByQty from '../../graphql/queries/priceByQty.query'
import messages from '../../config/messages'
import {
  getFinalBottlesAndCases,
  previousQtyUpdater,
  priceUpdater,
  removeTypenamesFromOrderDet,
} from '../../utils'
import { DesktopView, MobileView } from '../../config/mediaQuery'

type Props = {
  item: GridItem,
  moduleName: string,
  onItemAdded?:
    | ((ActionStatus) => typeof undefined)
    | Array<?(ActionStatus) => typeof undefined>,
  itemAddedStatus?: ActionStatus,
  onItemUpdated?: ((any, any, any) => mixed) | Array<?(any, any, any) => mixed>,
  buttonText?: string,
  buttonTextLoading?: string,
  buttonTextCompleted?: string,
  buttonIcon?: string,
  successMsg?: string,
  errorMsg?: string,
  title?: string,
  selectedOrderType?: string,
  saveChangesRemotely?: boolean,
  activeOrder?: any,
  updaters?: [(item: GridItem) => mixed],
  onComplete?: () => mixed,
  showRemoveInsteadOfSave?: boolean,
}

type State = {
  dirty: boolean,
  errorMessage: string,
  maxBottles: number,
  maxCases: number,
  newPrice: number,
  closing: boolean,
}

const FormStatusMessage = ({
  visible,
  isAllowedToEditTheForm,
  hasProductLimitBeenReached,
  isAboveLimit,
  previousQtyAdded,
  cases,
  bottles,
}: {
  visible: boolean,
  isAllowedToEditTheForm: boolean,
  hasProductLimitBeenReached: boolean,
  isAboveLimit: boolean,
  previousQtyAdded: number,
  cases: number,
  bottles: number,
}) => {
  if (!visible) return null

  if (isAllowedToEditTheForm) {
    if (isAboveLimit) {
      return (
        <Label bsStyle="info">
          {messages.addItemToOrder.aboveLimit}{' '}
          <InlineMessage value={Math.abs(cases)} label="case" />
          {' and '}
          <InlineMessage value={Math.abs(bottles)} label="bottle" />
        </Label>
      )
    }

    if (hasProductLimitBeenReached) {
      return (
        <Label bsStyle="info">{messages.addItemToOrder.limitReached}</Label>
      )
    }

    return (
      <Label bsStyle="info">
        <div style={{ display: 'flex', alignItems: 'center'}}>
          <img src='assets/information-circle.svg' style={{ marginRight: 5 }} alt='information icon'/>
          {messages.addItemToOrder.currentLimitDescription}
          {': '}
          <InlineMessage value={cases} label="case" />
          {' and '}
          <InlineMessage value={bottles} label="bottle" />
        </div>
      </Label>
    )
  }

  return <Label bsStyle="warning">{messages.addItemToOrder.noStock}</Label>
}

const InlineMessage = ({ value, label }: { value: number, label: string }) => {
  return (
    <React.Fragment>
      {value} {pluralize(label, value)}
    </React.Fragment>
  )
}

class AddItemHistoryToOrderAction extends React.Component<
  CommonActionProps & Props,
  State,
> {
  static defaultProps = {
    size: 'xsmall',
    buttonText: 'Add to Order',
    buttonTextLoading: 'Adding item...',
    buttonTextCompleted: 'Item added!',
    buttonIcon: 'plus',
    successMsg: messages.addItemToOrder.success,
    fail: messages.addItemToOrder.fail,
    title: 'Add to Order',
    saveChangesRemotely: true,
  }

  closingRequested: TimeoutID
  closingSuccessed: TimeoutID

  constructor(props: CommonActionProps & Props) {
    super(props)

    this.state = {
      dirty: false,
      errorMessage: '',
      maxBottles: 0,
      maxCases: 0,
      newPrice: 0,
      closing: false,
    }
  }

  onAddCompleted = ({
    isValid,
    data,
    onToggleVisibility,
    values,
    resetForm,
  }: {
    isValid: boolean,
    data: any,
    values: any,
    onToggleVisibility: () => mixed,
    resetForm: () => mixed,
  }) => {
    // data.addItemToOrder = null only when the item is getting removed
    if (
      (data.addItemToOrder !== null && !isValid) ||
      (data.addItemToOrder && data.addItemToOrder.error)
    ) {
      return
    }

    this.setState({
      closing: true,
    })

    // local clean up
    // this.resetForm();

    if (this.closingRequested) {
      clearTimeout(this.closingRequested)
    }

    this.onExit({
      onToggleVisibility,
      loading: false,
      dirty: false,
      values,
      resetForm,
      data: data.addItemToOrder,
      needRefetch: true,
    })

  }

  resetForm = () => {
    this.setState({
      dirty: false,
      errorMessage: '',
      maxBottles: 0,
      maxCases: 0,
      newPrice: 0,
    })

    if (this.closingSuccessed) {
      clearTimeout(this.closingSuccessed)
    }

    this.closingSuccessed = setTimeout(() => {
      this.setState({
        closing: false,
      })
    }, 500)
  }

  onExit = (options: {
    onToggleVisibility: () => mixed,
    resetForm: () => mixed,
    dirty: boolean,
    loading: boolean,
    values?: any,
    force?: boolean,
    cancelled?: boolean,
    data?: any,
    needRefetch?: boolean,
  }) => {
    const {
      onToggleVisibility,
      dirty,
      force,
      values,
      resetForm,
      loading,
      cancelled = false,
      data = {},
      needRefetch = false,
    } = options

    const { OrderDet, Cases, Bottles } = data || {}

    // if the mutation hasn't finished this will avoid closing the modal until
    // the transaction has been completed.
    if (loading) return

    if (dirty && !force) {
      this.setState({
        dirty,
      })

      return
    }

    // formik's reset form
    resetForm()

    // local clean up
    this.resetForm()

    /**
     * Only do something when the user didn't cancelled the action
     */
    if (
      !cancelled &&
      typeof this.props.onItemUpdated !== 'undefined' &&
      values &&
      this.props.item
    ) {
      const {
        Bottles: finalBottles,
        Cases: finalCases,
      } = getFinalBottlesAndCases({
        item: this.props.item,
        values,
      })

      /**
       * Since there is quite a bit of logic for distributing Qtys the server
       * is taking care of applying it.
       */
      const itemFromServer =
        OrderDet && OrderDet.find((i) => i.Code === this.props.item.Code)

      /**
       * external updaters than can be sent to this component to perform other
       * kind of operations over the row
       */
      const updaters = this.props.updaters
        ? [
            {
              updater: this.props.updaters,
            },
          ]
        : []

      const updaterArrayParam = [
        {
          fieldName: 'Cases',
          value: finalCases,
        },
        {
          fieldName: 'Bottles',
          value: finalBottles,
        },
        {
          updater: priceUpdater({
            Price: values.Price,
          }),
        },
        {
          updater: previousQtyUpdater({
            Cases: finalCases,
            Bottles: finalBottles,
            OrderType:
              this.props.selectedOrderType ||
              (this.props.activeOrder && this.props.activeOrder.OrderTypeValue),
          }),
        },
        ...updaters,
      ]

      const extendedItem = itemFromServer
        ? {
            Cases,
            Bottles,
            Mobo: itemFromServer.Mobo,
            MoboQty: itemFromServer.MoboQty,
          }
        : {}

      if (Array.isArray(this.props.onItemUpdated)) {
        this.props.onItemUpdated.forEach((fn) => {
          typeof fn === 'function' &&
            fn(updaterArrayParam, this.props.item.Code, {
              ...this.props.item,
              ...extendedItem,
            })
        })
      } else if (typeof this.props.onItemUpdated === 'function') {
        this.props.onItemUpdated(updaterArrayParam, this.props.item.Code, {
          ...this.props.item,
          ...extendedItem,
        })
      }

      if (needRefetch) {
        this.props.moduleRefetch()
      }
    }

    if (typeof this.props.onItemAdded === 'function') {
      this.props.onItemAdded('completed')
    } else if (Array.isArray(this.props.onItemAdded)) {
      this.props.onItemAdded.forEach((fn) => {
        typeof fn === 'function' && fn('completed')
      })
    }

    /**
     * The reason we are checking for the existence of  this.props.onComplete is because
     * if it exists, we won't need to to any kind of client side update and the server
     * will take care of it. This is gonna happen on situations where a change on a single
     * item might affect the pricing of others, like in the case of Orders from NJ where
     * the price of each item depends (among other things) on the order total amount of items
     */
    if (typeof this.props.onComplete === 'function' && !cancelled) {
      this.props.onComplete()
    }

    onToggleVisibility()
  }

  onRemove = async ({ mutation, setFieldValue }: any) => {
    try {
      setFieldValue('Cases', 0)
      setFieldValue('Bottles', 0)

      const { data } = await mutation({
        variables: {
          input: {
            Cases: 0,
            Bottles: 0,
            Code: this.props.item && this.props.item.Code,
            moduleName: this.props.moduleName,
            Uom: this.props.item && this.props.item.Uom,
          },
        },
      })

      const { addItemToOrder } = data

      if (addItemToOrder && addItemToOrder.error) {
        const { extraInfo, errorMessage } = addItemToOrder.error.data

        this.setState({
          errorMessage,
          maxBottles: extraInfo.Bottles,
          maxCases: extraInfo.Cases,
          newPrice: extraInfo.Price,
        })
      }
    } catch (e) {
      const cleanedMessage = e.message
        ? e.message.replace('GraphQL error: ', '')
        : ''

      this.setState({
        errorMessage: cleanedMessage,
      })
    }
  }

  onSubmit = async ({ mutation }: any) => {
    try {
      const { data } = await mutation()

      const { addItemToOrder } = data

      if (addItemToOrder && addItemToOrder.error) {
        const { extraInfo, errorMessage } = addItemToOrder.error.data

        this.setState({
          errorMessage,
          maxBottles: extraInfo.Bottles,
          maxCases: extraInfo.Cases,
          newPrice: extraInfo.Price,
        })
      }
    } catch (e) {
      const cleanedMessage = e.message
        ? e.message.replace('GraphQL error: ', '')
        : ''

      this.setState({
        errorMessage: cleanedMessage,
      })
    }
  }

  cancelConfirmPrompt = () => {
    this.setState({
      dirty: false,
    })
  }

  getInputFieldProps = ({
    name,
    label,
    value,
    errors,
    touched,
    isReadOnly,
    isItemAdded,
    setFieldValue,
    setFieldTouched,
    loading,
    onBlur: onBlurFn,
  }: any) => {
    if (loading) {
      return {
        label,
        name,
        value: '...',
        placeholder: '',
        readOnly: true,
      }
    }

    if (isReadOnly) {
      return {
        label,
        name,
        value,
        placeholder: '',
        readOnly: true,
      }
    }

    return {
      label,
      name,
      placeholder: '',
      errors,
      touched,
      readOnly: false,
      type: 'number',
      fieldProps: {
        step: '1',
        min: '0',
        disabled: isItemAdded,
        onBlur: async (v: any) => {
          v.persist()

          if (typeof onBlurFn === 'function') {
            const { data } = await onBlurFn()

            setFieldValue('Price', data.priceByQty)
          }

          if (v.target.value === '') {
            setFieldValue(name, 0)
          }

          setFieldTouched(name, true)
        },
      },
    }
  }

  getOrderNo = (item?: GridItem) => {
    if (item && item.OrderNo) {
      if (typeof item.OrderNo === 'string') {
        return {
          OrderNoMobo: item.OrderNo,
        }
      }

      return {
        OrderNo: item.OrderNo,
      }
    }

    return {}
  }

  render() {
    return (
      <ToggleController>
        {({ onToggleVisibility, visible }) => {
          const item = this.props.item || {}

          /**
           * This is because there might be some Order Types that actually don't have
           * any kind of quantity validations (like "Pick Up" or "Unsaleable")
           */
          const OrderType =
            this.props.selectedOrderType ||
            (this.props.activeOrder && this.props.activeOrder.OrderTypeValue)

          const OrderDate =
            this.props.selectedOrderDate ||
            (this.props.activeOrder && this.props.activeOrder.OrderDate)

          const itemMaxQty =
            item.maxItemQty &&
            Array.isArray(item.maxItemQty.byOrderType) &&
            item.maxItemQty.byOrderType.find((i) => i.id === OrderType)

          const maxItemQty = itemMaxQty
            ? {
                ...itemMaxQty,
                PreviousAdd: item.maxItemQty.PreviousAdd,
              }
            : { PreviousAdd: {}, Qty: -1, Cases: -1, Bottles: -1 }

          const previousQtyAdded =
            maxItemQty.PreviousAdd && maxItemQty.PreviousAdd.Qty
          const wasItemAddedBefore = previousQtyAdded > 0

          const showMaxLimits = !(OrderType === 'P' || OrderType === 'U')

          const stockAvailable =
            maxItemQty.Qty > 0 ||
            (previousQtyAdded > 0 && previousQtyAdded + maxItemQty.Qty !== 0)

          const hasProductLimitBeenReached =
            maxItemQty.Qty === 0 && previousQtyAdded > 0
          const isAboveLimit = maxItemQty.Qty < 0 && previousQtyAdded > 0
          const isAllowedToEditTheForm = stockAvailable

          return (
            <Formik
              initialValues={{
                Cases: maxItemQty.PreviousAdd && maxItemQty.PreviousAdd.Cases,
                Bottles:
                  maxItemQty.PreviousAdd && maxItemQty.PreviousAdd.Bottles,
                Price: item.Price && item.Price.value,
              }}
              enableReinitialize
              validate={(values) => {
                const errors = {}
                const { Uom = 1 } = item

                // The only way we have to compare these values is by passing everything
                // to the same unit of measure (since Qty itself might not work bc of rounding)
                // so we picked converting everything to Bottles.
                const localCasesToBottles = values.Cases * Uom
                const totalLocalBottles = localCasesToBottles + values.Bottles

                const previousAdd = maxItemQty.PreviousAdd || {}
                const previousCasesToBottles = previousAdd.Cases * Uom
                const previousBottles = previousAdd.Bottles
                const totalPreviousBottles =
                  previousCasesToBottles + previousBottles
                const totalQty = maxItemQty.Cases * Uom + maxItemQty.Bottles
                const totalLimit = totalQty + totalPreviousBottles

                if (values.Cases < 0) {
                  errors.Cases = messages.common.nonNegative
                }

                if (values.Bottles < 0) {
                  errors.Bottles = messages.common.nonNegative
                }

                if (totalLocalBottles > totalLimit) {
                  errors.Cases = messages.addItemToOrder.quantityExceeded
                  errors.Bottles = messages.addItemToOrder.quantityExceeded
                }

                if (values.Price === -1 || values.Price === null) {
                  errors.Price = messages.common.noPriceAvailable
                }

                return errors
              }}
            >
              {({
                values,
                errors,
                touched,
                isValid,
                isInitialValid,
                setFieldValue,
                setFieldTouched,
                resetForm,
                dirty,
              }) => (
                <Mutation
                  mutation={addItemToOrderMutation}
                  variables={{
                    input: {
                      ...values,
                      Code: this.props.item && this.props.item.Code,
                      Uom: this.props.item && this.props.item.Uom,
                      Description:
                        this.props.item && this.props.item.Description,
                      Price: item.Price && item.Price.value,
                      wasItemAddedBefore,
                      Mobo: this.props.item && this.props.item.Mobo,
                      moduleName: this.props.moduleName,
                      saveChangesRemotely: this.props.saveChangesRemotely,
                      OrderDet: removeTypenamesFromOrderDet(
                        this.props.activeOrder && this.props.activeOrder.Det,
                      ),
                      QtyA: this.props.item && this.props.item.QtyA,
                      OrderType,
                      maxItemQty: this.props.item && this.props.item.maxItemQty,
                      ...this.getOrderNo(this.props.item),
                    },
                  }}
                  onCompleted={(onCompleteData) => {
                    this.onAddCompleted({
                      data: onCompleteData,
                      isValid,
                      onToggleVisibility,
                      resetForm,
                      values,
                    })
                  }}
                  errorPolicy="none"
                  refetchQueries={() => {
                    return [
                      {
                        query: getActiveOrderQuery,
                      },
                    ]
                  }}
                >
                  {(mutation, { loading, error, data: dataMutation }: any) => (
                    <StatusController
                      hasError={
                        !!error ||
                        (dataMutation &&
                          dataMutation.addItemToOrder &&
                          dataMutation.addItemToOrder.error)
                      }
                      loading={loading}
                    >
                      {({ status }) => {
                        const isItemAdded =
                          status === 'completed' ||
                          this.props.itemAddedStatus === 'completed' ||
                          this.state.closing

                        return (
                          <React.Fragment>
                            <DesktopView>
                              <ButtonAction
                                bsSize={this.props.size}
                                disabled={this.props.disabled || status !== ''}
                                onClick={onToggleVisibility}
                              >
                                <Glyphicon glyph={this.props.buttonIcon} />{' '}
                                <span>
                                  {(status === '' || status === 'error') &&
                                    this.props.buttonText}
                                  {status === 'loading' &&
                                    this.props.buttonTextLoading}
                                  {status === 'completed' &&
                                    this.props.buttonTextCompleted}
                                </span>
                              </ButtonAction>
                            </DesktopView>
                            <MobileView>
                              <button
                                disabled={this.props.disabled || status !== ''}
                                onClick={onToggleVisibility}
                              >
                                <img src='assets/plus.svg' alt='add icon'/>
                                {(status === '' || status === 'error') &&
                                  this.props.buttonText}
                                {status === 'loading' &&
                                  this.props.buttonTextLoading}
                                {status === 'completed' &&
                                  this.props.buttonTextCompleted}
                              </button>
                            </MobileView>
                            <MobileView>
                              {(isMobile) => (
                                <Modal
                                  show={visible}
                                  onHide={() =>
                                    this.onExit({
                                      onToggleVisibility,
                                      dirty,
                                      resetForm,
                                      loading,
                                      cancelled: true,
                                    })
                                  }
                                  className="modal--form"
                                  dialogClassName={isMobile ? "add-to-order-modal" : ''}
                                >
                                  <Modal.Header closeButton>
                                    <Modal.Title>
                                      {previousQtyAdded
                                        ? messages.addItemToOrder.editTitle
                                        : messages.addItemToOrder.addTitle}
                                    </Modal.Title>
                                  </Modal.Header>
                                  {this.props.item && (
                                    <Form>
                                      <Modal.Body>
                                        {loading && (
                                          <Loading
                                            message={
                                              loading &&
                                                this.props.saveChangesRemotely
                                                ? messages.addItemToOrder
                                                  .validatingDataAgainstServer
                                                : ''
                                            }
                                          />
                                        )}
                                        {this.state.dirty && (
                                          <Prompt
                                            onCancel={this.cancelConfirmPrompt}
                                            onConfirm={() =>
                                              this.onExit({
                                                onToggleVisibility,
                                                resetForm,
                                                loading,
                                                values,
                                                dirty: false,
                                                force: true,
                                                cancelled: true,
                                              })
                                            }
                                            message={messages.common.unsaved}
                                          />
                                        )}
                                        <Query
                                          query={getPriceByQty}
                                          fetchPolicy="network-only"
                                          variables={{
                                            input: {
                                              Code:
                                                this.props.item &&
                                                this.props.item.Code,
                                              Price:
                                                this.props.item &&
                                                this.props.item.Price &&
                                                this.props.item.Price.rawValue,
                                              OrderDet: removeTypenamesFromOrderDet(
                                                this.props.activeOrder &&
                                                this.props.activeOrder.Det,
                                              ),
                                              pricingTiers:
                                                this.props.item &&
                                                this.props.item.Price &&
                                                this.props.item.Price.pricingTiers,
                                              inventory: {
                                                product:
                                                  this.props.item &&
                                                  this.props.item.Price &&
                                                  this.props.item.Price.product,
                                                Main:
                                                  this.props.item &&
                                                  this.props.item.Price &&
                                                  this.props.item.Price
                                                    .mixedSizeItems,
                                              },
                                              Cases: values.Cases,
                                              Bottles: values.Bottles,
                                              Level:
                                                this.props.activeOrder &&
                                                this.props.activeOrder.Account &&
                                                this.props.activeOrder.Account
                                                  .Level,
                                              Coop:
                                                this.props.activeOrder &&
                                                this.props.activeOrder.Coop,
                                              moduleName: this.props.moduleName,
                                              OrderType,
                                              OrderDate,
                                            },
                                          }}
                                        >
                                          {({
                                            data: dataRefetch,
                                            refetch: priceRefetch,
                                            loading: priceLoading,
                                          }: any) => {
                                            const codeSingleField = <SingleField
                                              label="Item Code"
                                              name="Code"
                                              value={this.props.item.Code}
                                              placeholder=""
                                              readOnly
                                              inline={!isMobile}
                                            />
                                            const descriptionField = <DescriptionField
                                              label="Item Description"
                                              name="Description"
                                              value={this.props.item.Description}
                                              placeholder=""
                                              readOnly
                                              inline={!isMobile}
                                            />
                                            const priceSingleField = <SingleField
                                              {...this.getInputFieldProps({
                                                name: 'Price',
                                                label: 'Price',
                                                value:
                                                  (dataRefetch &&
                                                    dataRefetch.priceByQty) ||
                                                  values.Price,
                                                errors,
                                                touched,
                                                isReadOnly: true,
                                                isItemAdded,
                                                setFieldValue,
                                                setFieldTouched,
                                                isValidating: priceLoading,
                                              })}
                                              kind={
                                                item.Price !== -1 ? 'money' : ''
                                              }
                                              inline={!isMobile}
                                            />
                                            const casesSingleField = <SingleField
                                              {...this.getInputFieldProps({
                                                name: 'Cases',
                                                label: 'Cases',
                                                value: values.Cases,
                                                errors,
                                                touched,
                                                isReadOnly: !isAllowedToEditTheForm,
                                                isItemAdded,
                                                setFieldValue,
                                                setFieldTouched,
                                                onBlur: priceRefetch,
                                              })}
                                              inline={isMobile}
                                              counterButtons
                                              setFieldValue={setFieldValue}
                                              values={values}
                                            />
                                            const bottlesSingleField = <SingleField
                                              {...this.getInputFieldProps({
                                                name: 'Bottles',
                                                label: 'Bottles',
                                                value: values.Bottles,
                                                errors,
                                                touched,
                                                isReadOnly: !isAllowedToEditTheForm,
                                                isItemAdded,
                                                setFieldValue,
                                                setFieldTouched,
                                                onBlur: priceRefetch,
                                              })}
                                              inline={isMobile}
                                              counterButtons
                                              setFieldValue={setFieldValue}
                                              values={values}
                                            />
                                            return (
                                              <React.Fragment>
                                                <DesktopView>
                                                  {codeSingleField}
                                                  {descriptionField}
                                                  {priceSingleField}
                                                  {casesSingleField}
                                                  {bottlesSingleField}
                                                </DesktopView>
                                                <MobileView>
                                                  <div className='modal-multiple-fields-inline'>
                                                    {codeSingleField}
                                                    {priceSingleField}
                                                  </div>
                                                  {descriptionField}
                                                  <div className='modal-counter-buttons-container'>
                                                    {casesSingleField}
                                                    {bottlesSingleField}
                                                  </div>
                                                </MobileView>
                                              </React.Fragment>
                                            )
                                          }}
                                        </Query>
                                        <div
                                          css={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            height: '15px',
                                            transition: 'opacity 0.5s ease-in-out',
                                            opacity: 1,
                                          }}
                                        >
                                          {!this.props.showRemoveInsteadOfSave && (
                                            <FormStatusMessage
                                              visible={showMaxLimits}
                                              isAllowedToEditTheForm={
                                                isAllowedToEditTheForm
                                              }
                                              hasProductLimitBeenReached={
                                                hasProductLimitBeenReached
                                              }
                                              isAboveLimit={isAboveLimit}
                                              previousQtyAdded={previousQtyAdded}
                                              cases={
                                                this.state.maxCases ||
                                                maxItemQty.Cases
                                              }
                                              bottles={
                                                this.state.maxBottles ||
                                                maxItemQty.Bottles
                                              }
                                            />
                                          )}
                                        </div>
                                      </Modal.Body>
                                      <Modal.Footer>
                                        <Row
                                          css={{
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Col
                                            css={{
                                              marginRight: 'auto',
                                            }}
                                          >
                                            {(status === 'completed' ||
                                              status === '') && (
                                                <StatusMessage status={status}>
                                                  {this.props.successMsg}
                                                </StatusMessage>
                                              )}
                                            {(status === 'error' ||
                                              status === '') && (
                                                <StatusMessage status={status}>
                                                  {this.state.errorMessage ||
                                                    this.props.errorMsg}
                                                </StatusMessage>
                                              )}
                                            {item.Price === -1 && (
                                              <StatusMessage
                                                status={'error'}
                                                autoHide={false}
                                              >
                                                {
                                                  messages.common
                                                    .noPriceAvailableError
                                                }
                                              </StatusMessage>
                                            )}
                                          </Col>
                                          <Col
                                            css={{
                                              marginLeft: 'auto',
                                            }}
                                          >
                                            {this.props.showRemoveInsteadOfSave ? (
                                              <Button
                                                onClick={() => {
                                                  this.onRemove({
                                                    mutation,
                                                    setFieldValue,
                                                  })
                                                }}
                                              >
                                                Remove from order
                                              </Button>
                                            ) : (
                                              <>
                                                <DesktopView>
                                                  <Button
                                                    disabled={
                                                      status !== '' ||
                                                      (!isInitialValid && !isValid) ||
                                                      isItemAdded
                                                    }
                                                    onClick={() =>
                                                      this.onSubmit({
                                                        mutation,
                                                      })
                                                    }
                                                  >
                                                    {(status === '' ||
                                                      status === 'error') &&
                                                      'Save'}
                                                    {status === 'loading' &&
                                                      'Saving...'}
                                                    {status === 'completed' && 'Saved!'}
                                                  </Button>
                                                </DesktopView>
                                                <MobileView>
                                                  <div className='columns-bottom-button-group'>
                                                    <button
                                                    type='button'
                                                      onClick={onToggleVisibility}
                                                    >
                                                      Cancel
                                                    </button>
                                                    <button
                                                      disabled={
                                                        status !== "" ||
                                                        (!isInitialValid && !isValid) ||
                                                        isItemAdded
                                                      }
                                                      onClick={() =>
                                                        this.onSubmit({
                                                          mutation
                                                        })
                                                      }
                                                    >
                                                      {(status === "" ||
                                                        status === "error") &&
                                                        "Save"}
                                                      {status === "loading" &&
                                                        "Saving..."}
                                                      {status === "completed" && "Saved!"}
                                                    </button>
                                                  </div>
                                                </MobileView>
                                              </>
                                            )}
                                          </Col>
                                        </Row>
                                      </Modal.Footer>
                                    </Form>
                                  )}
                                </Modal>
                              )}
                            </MobileView>
                          </React.Fragment>
                        )
                      }}
                    </StatusController>
                  )}
                </Mutation>
              )}
            </Formik>
          )
        }}
      </ToggleController>
    )
  }
}

export default AddItemHistoryToOrderAction
